import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { labelFor, shippingModeLabels, shippingProviderLabels, statusLabels } from './labels';

const money = new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ' });
const number = new Intl.NumberFormat('es-GT');

const safeValue = (value, fallback = '') => (value === undefined || value === null ? fallback : value);

const formatDateTime = (value) => {
    if (!value) return '';
    return new Intl.DateTimeFormat('es-GT', {
        dateStyle: 'short',
        timeStyle: 'short',
    }).format(new Date(value));
};

const getPeriodLabel = (filters) => {
    const location = [
        filters.department,
        filters.municipality,
    ].filter(Boolean).join(' / ');
    const suffix = location ? ` - ${location}` : '';
    if (filters.mode === 'day' && filters.day) return `Dia ${filters.day}${suffix}`;
    if (filters.mode === 'month' && filters.month) return `Mes ${filters.month}${suffix}`;
    if (filters.mode === 'range') {
        return `Rango ${filters.from || 'inicio'} a ${filters.to || 'hoy'}${suffix}`;
    }
    return `Todos los registros${suffix}`;
};

const fetchImageAsDataUrl = async (url) => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
    });
};

export const buildTopProducts = (sales) => {
    const products = new Map();

    sales.forEach((sale) => {
        (sale.items || []).forEach((item) => {
            const current = products.get(item.code) || {
                code: item.code,
                units: 0,
                salesTotal: 0,
                costTotal: 0,
                profitTotal: 0,
            };
            current.units += 1;
            current.salesTotal += Number(item.salePrice || 0);
            current.costTotal += Number(item.costAtSale || 0);
            current.profitTotal = current.salesTotal - current.costTotal;
            products.set(item.code, current);
        });
    });

    return Array.from(products.values()).sort((a, b) => b.units - a.units || b.salesTotal - a.salesTotal);
};

const buildSummary = (sales) => sales.reduce((summary, sale) => {
    summary.orders += 1;
    summary.sales += Number(sale.totals?.saleTotal || 0);
    summary.jewelry += Number(sale.totals?.jewelrySalesTotal || 0);
    summary.shipping += Number(sale.totals?.shippingCustomerTotal || 0);
    summary.commission += Number(sale.totals?.codCommission || 0);
    summary.netProfit += Number(sale.totals?.netProfit || 0);
    return summary;
}, {
    orders: 0,
    sales: 0,
    jewelry: 0,
    shipping: 0,
    commission: 0,
    netProfit: 0,
});

const saleCodes = (sale) => (sale.items || []).map((item) => item.code).join(', ');

const buildLocationSummary = (sales) => {
    const rows = new Map();

    sales.forEach((sale) => {
        const department = sale.customerSnapshot?.location?.department || 'Sin departamento';
        const municipality = sale.customerSnapshot?.location?.municipality || 'Sin municipio';
        const key = `${department}|${municipality}`;
        const current = rows.get(key) || { department, municipality, orders: 0, sales: 0, netProfit: 0 };
        current.orders += 1;
        current.sales += Number(sale.totals?.saleTotal || 0);
        current.netProfit += Number(sale.totals?.netProfit || 0);
        rows.set(key, current);
    });

    return Array.from(rows.values()).sort((a, b) => b.sales - a.sales);
};

const addPdfFooter = (doc) => {
    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
        doc.setPage(page);
        doc.setFontSize(8);
        doc.setTextColor(112, 121, 148);
        doc.text(`Zashani Ventas - Pagina ${page} de ${pageCount}`, 14, 288);
        doc.text(new Date().toLocaleString('es-GT'), 196, 288, { align: 'right' });
    }
};

export const exportSalesReportPdf = async ({ sales, filters, user }) => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
    const logo = await fetchImageAsDataUrl('/brand/zashani-logo-wide.png');
    const summary = buildSummary(sales);
    const topProducts = buildTopProducts(sales);
    const locationSummary = buildLocationSummary(sales);

    doc.setFillColor(7, 11, 26);
    doc.rect(0, 0, 216, 38, 'F');
    doc.addImage(logo, 'PNG', 14, 9, 58, 13);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.text('Reporte de ventas', 202, 15, { align: 'right' });
    doc.setFontSize(9);
    doc.setTextColor(216, 194, 154);
    doc.text(getPeriodLabel(filters), 202, 23, { align: 'right' });
    doc.setTextColor(230, 233, 245);
    doc.text(`Generado por: ${user?.name || 'Usuario'}`, 14, 32);
    doc.text(`Hora: ${new Date().toLocaleString('es-GT')}`, 202, 32, { align: 'right' });

    const cards = [
        ['Pedidos', number.format(summary.orders)],
        ['Total vendido', money.format(summary.sales)],
        ['Joyas', money.format(summary.jewelry)],
        ['Envios', money.format(summary.shipping)],
        ['Comision COD', money.format(summary.commission)],
        ['Ganancia neta', money.format(summary.netProfit)],
    ];

    let x = 14;
    let y = 46;
    cards.forEach(([label, value], index) => {
        if (index === 3) {
            x = 14;
            y += 23;
        }
        doc.setFillColor(247, 248, 252);
        doc.roundedRect(x, y, 60, 17, 3, 3, 'F');
        doc.setTextColor(112, 121, 148);
        doc.setFontSize(7);
        doc.text(label.toUpperCase(), x + 4, y + 6);
        doc.setTextColor(29, 40, 95);
        doc.setFontSize(12);
        doc.text(value, x + 4, y + 13);
        x += 65;
    });

    autoTable(doc, {
        startY: 96,
        head: [['Fecha', 'Cliente', 'Departamento', 'Municipio', 'Codigos', 'Total', 'Envio', 'Estado', 'Ganancia']],
        body: sales.map((sale) => [
            formatDateTime(sale.createdAt),
            safeValue(sale.customerSnapshot?.fullName),
            safeValue(sale.customerSnapshot?.location?.department),
            safeValue(sale.customerSnapshot?.location?.municipality),
            saleCodes(sale),
            money.format(sale.totals?.saleTotal || 0),
            `${labelFor(shippingProviderLabels, sale.shipping?.provider)} ${labelFor(shippingModeLabels, sale.shipping?.mode)}`,
            labelFor(statusLabels, sale.status),
            money.format(sale.totals?.netProfit || 0),
        ]),
        styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak' },
        headStyles: { fillColor: [29, 40, 95], textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [247, 248, 252] },
        columnStyles: {
            4: { cellWidth: 24 },
            5: { halign: 'right' },
            8: { halign: 'right' },
        },
    });

    autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 10,
        head: [['#', 'Departamento', 'Municipio', 'Pedidos', 'Ventas', 'Ganancia']],
        body: locationSummary.slice(0, 20).map((item, index) => [
            index + 1,
            item.department,
            item.municipality,
            item.orders,
            money.format(item.sales),
            money.format(item.netProfit),
        ]),
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [29, 40, 95], textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [247, 248, 252] },
    });

    autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 10,
        head: [['#', 'Codigo', 'Unidades', 'Total vendido', 'Costo', 'Margen estimado']],
        body: topProducts.slice(0, 25).map((product, index) => [
            index + 1,
            product.code,
            product.units,
            money.format(product.salesTotal),
            money.format(product.costTotal),
            money.format(product.profitTotal),
        ]),
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [7, 11, 26], textColor: [216, 194, 154] },
        alternateRowStyles: { fillColor: [247, 248, 252] },
    });

    addPdfFooter(doc);
    doc.save(`reporte-ventas-zashani-${Date.now()}.pdf`);
};

export const exportSalesReportExcel = ({ sales, filters, user }) => {
    const summary = buildSummary(sales);
    const topProducts = buildTopProducts(sales);
    const locationSummary = buildLocationSummary(sales);
    const workbook = XLSX.utils.book_new();

    const summaryRows = [
        ['Reporte', 'Ventas Zashani'],
        ['Periodo', getPeriodLabel(filters)],
        ['Generado por', user?.name || 'Usuario'],
        ['Hora', new Date().toLocaleString('es-GT')],
        [],
        ['Pedidos', summary.orders],
        ['Total vendido', summary.sales],
        ['Joyas', summary.jewelry],
        ['Envios', summary.shipping],
        ['Comision COD', summary.commission],
        ['Ganancia neta', summary.netProfit],
    ];

    const salesRows = sales.map((sale) => ({
        Fecha: formatDateTime(sale.createdAt),
        Cliente: sale.customerSnapshot?.fullName || '',
        Telefono: sale.customerSnapshot?.phone || '',
        Departamento: sale.customerSnapshot?.location?.department || '',
        Municipio: sale.customerSnapshot?.location?.municipality || '',
        Zona: sale.customerSnapshot?.location?.zone || '',
        Direccion: sale.customerSnapshot?.address || '',
        Codigos: saleCodes(sale),
        Joyas: sale.totals?.jewelrySalesTotal || 0,
        EnvioCliente: sale.totals?.shippingCustomerTotal || 0,
        TotalVenta: sale.totals?.saleTotal || 0,
        Deposito: sale.totals?.depositTotal || 0,
        Pendiente: sale.totals?.pendingBalance || 0,
        ComisionCOD: sale.totals?.codCommission || 0,
        GananciaNeta: sale.totals?.netProfit || 0,
        Envio: labelFor(shippingProviderLabels, sale.shipping?.provider),
        Modalidad: labelFor(shippingModeLabels, sale.shipping?.mode),
        Estado: labelFor(statusLabels, sale.status),
        RegistradoPor: sale.createdBy?.name || '',
    }));

    const productRows = topProducts.map((product, index) => ({
        Ranking: index + 1,
        Codigo: product.code,
        Unidades: product.units,
        TotalVendido: product.salesTotal,
        Costo: product.costTotal,
        MargenEstimado: product.profitTotal,
    }));

    const locationRows = locationSummary.map((item, index) => ({
        Ranking: index + 1,
        Departamento: item.department,
        Municipio: item.municipality,
        Pedidos: item.orders,
        Ventas: item.sales,
        GananciaNeta: item.netProfit,
    }));

    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(summaryRows), 'Resumen');
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(salesRows), 'Ventas');
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(locationRows), 'Ubicaciones');
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(productRows), 'Productos');
    XLSX.writeFile(workbook, `reporte-ventas-zashani-${Date.now()}.xlsx`);
};
